import { Badge, DataTable, type TanstackReactTable } from "@devhop/ui";
import { useQueries } from "@tanstack/react-query";
import { EditIcon, PlusIcon, StoreIcon } from "lucide-react";
import { formatPrice } from "@/shared/lib/format-price";
import { type FeeRuleDTO, feesApi } from "../api/client";

function formatValue(row: FeeRuleDTO): string {
  if (row.feeMode === "percent") {
    return `${Number(row.value).toFixed(2)}%`;
  }
  return formatPrice(Number(row.value), "", {
    appendCurrency: false,
    locale: "en-US",
  });
}

type ShopRow = {
  shopId: string;
  shopName: string;
  marketplace: FeeRuleDTO | null;
  mechanic: FeeRuleDTO | null;
};

type ShopFeeViewProps = {
  canManage: boolean;
  offset: number;
  limit: number;
  onEdit: (rule: FeeRuleDTO) => void;
  onAddForShop: (shopId: string, shopName: string) => void;
  onPaginationChange: (offset: number, limit: number) => void;
};

export function ShopFeeView({
  canManage,
  offset,
  limit,
  onEdit,
  onAddForShop,
  onPaginationChange,
}: ShopFeeViewProps) {
  const [shopsQ, rulesMq, rulesMechQ] = useQueries({
    queries: [
      {
        queryKey: ["fees", "lookup", "shops", "paginated", offset, limit],
        queryFn: () => feesApi.lookupShops({ limit, skip: offset }),
      },
      {
        queryKey: ["fees", "list", "marketplace_commission", "scope_shop"],
        queryFn: () =>
          feesApi.list({
            feeType: "marketplace_commission",
            limit: 500,
            offset: 0,
            filters: [{ field: "scope", op: "eq", value: "shop" }],
          }),
      },
      {
        queryKey: ["fees", "list", "mechanic_commission", "scope_shop"],
        queryFn: () =>
          feesApi.list({
            feeType: "mechanic_commission",
            limit: 500,
            offset: 0,
            filters: [{ field: "scope", op: "eq", value: "shop" }],
          }),
      },
    ],
  });

  const shops = shopsQ.data?.items ?? [];
  const totalCount = shopsQ.data?.total ?? 0;
  const rulesM = rulesMq.data?.data ?? [];
  const rulesMech = rulesMechQ.data?.data ?? [];

  const byScopeId = (list: FeeRuleDTO[]): Map<string, FeeRuleDTO> =>
    new Map(list.filter((r) => r.scopeId).map((r) => [r.scopeId as string, r]));

  const mapM = byScopeId(rulesM);
  const mapMech = byScopeId(rulesMech);

  const rows: ShopRow[] = shops.map((s) => ({
    shopId: s.id,
    shopName: s.name,
    marketplace: mapM.get(s.id) ?? null,
    mechanic: mapMech.get(s.id) ?? null,
  }));

  const isLoading =
    shopsQ.isLoading || rulesMq.isLoading || rulesMechQ.isLoading;

  const columns: TanstackReactTable.ColumnDef<ShopRow>[] = [
    {
      id: "shop",
      header: "ຮ້ານຄ້າ",
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <StoreIcon className="size-4 text-muted-foreground" aria-hidden />
          <span className="font-medium">{row.original.shopName}</span>
          <Badge variant="secondary" className="text-xs">
            Override
          </Badge>
        </div>
      ),
      size: 260,
    },
    {
      id: "marketplace",
      header: "ຄ່າຄອມ Marketplace",
      cell: ({ row }) => {
        const r = row.original.marketplace;
        if (!r)
          return canManage ? (
            <button
              type="button"
              onClick={() =>
                onAddForShop(row.original.shopId, row.original.shopName)
              }
              className="inline-flex items-center gap-1 text-secondary text-sm hover:underline"
            >
              <PlusIcon className="size-4" aria-hidden />
              ຕັ້ງຄ່າ override
            </button>
          ) : (
            <span className="text-muted-foreground">—</span>
          );
        return (
          <div className="flex items-center gap-2">
            <span>{formatValue(r)}</span>
            {r.isActive ? (
              <Badge variant="success" className="text-xs">
                ໃຊ້ງານ
              </Badge>
            ) : (
              <Badge variant="destructive" className="text-xs">
                ປິດ
              </Badge>
            )}
            {canManage && (
              <button
                type="button"
                onClick={() => onEdit(r)}
                className="inline-flex items-center gap-1 text-secondary text-sm hover:underline"
              >
                <EditIcon className="size-4" aria-hidden />
                ແກ້ໄຂ
              </button>
            )}
          </div>
        );
      },
      size: 280,
    },
    {
      id: "mechanic",
      header: "ຄ່າທຳນຽມງານຊ່ງ",
      cell: ({ row }) => {
        const r = row.original.mechanic;
        if (!r)
          return canManage ? (
            <button
              type="button"
              onClick={() =>
                onAddForShop(row.original.shopId, row.original.shopName)
              }
              className="inline-flex items-center gap-1 text-secondary text-sm hover:underline"
            >
              <PlusIcon className="size-4" aria-hidden />
              ຕັ້ງຄ່າ override
            </button>
          ) : (
            <span className="text-muted-foreground">—</span>
          );
        return (
          <div className="flex items-center gap-2">
            <span>{formatValue(r)}</span>
            {r.isActive ? (
              <Badge variant="success" className="text-xs">
                ໃຊ້ງານ
              </Badge>
            ) : (
              <Badge variant="destructive" className="text-xs">
                ປິດ
              </Badge>
            )}
            {canManage && (
              <button
                type="button"
                onClick={() => onEdit(r)}
                className="inline-flex items-center gap-1 text-secondary text-sm hover:underline"
              >
                <EditIcon className="size-4" aria-hidden />
                ແກ້ໄຂ
              </button>
            )}
          </div>
        );
      },
      size: 280,
    },
  ];

  return (
    <DataTable<ShopRow, unknown>
      noDataMessage="ບໍ່ພົບຮ້ານຄ້າ"
      isLoading={isLoading}
      columns={columns}
      data={rows}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
    />
  );
}
